import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import PageLayout from "@/components/PageLayout";
import AuthModal from "@/components/AuthModal";
import MyStoriesWorkspace from "@/components/MyStoriesWorkspace";
import SavedCharactersLibrary from "@/components/SavedCharactersLibrary";
import SaveCharacterModal from "@/components/SaveCharacterModal";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Wand2, Star, ChevronDown, ChevronRight, Crown, Users, MapPin, Lightbulb, MessageSquare, Sparkles, Clock, XCircle, Eye, X, Palette, BookOpen, Lock, Globe, Dices, Camera, Upload, Library, Film, Mic } from "lucide-react";
import { toast } from "sonner";
import { STORY_CATEGORIES } from "@/constants/categories";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Collapsible Section Component - defined outside to prevent re-creation on every render
const CollapsibleSection = ({ title, icon: Icon, expanded, onToggle, children, badge }) => (
  <div className="border-b last:border-b-0" style={{ borderColor: 'var(--st-border-light)' }}>
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 hover:bg-orange-50/30 dark:hover:bg-white/5 transition-colors"
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
        <span className="font-semibold text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
          {title}
        </span>
        {badge && (
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--st-border-light)', color: 'var(--st-text-muted)' }}>
            {badge}
          </span>
        )}
      </div>
      <ChevronRight
        className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
        style={{ color: 'var(--st-text-muted)' }}
      />
    </button>
    {expanded && (
      <div className="px-4 pb-4 animate-in slide-in-from-top-1 duration-200">
        {children}
      </div>
    )}
  </div>
);

const CreatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [prompt, setPrompt] = useState("");
  const [ageRange, setAgeRange] = useState("3-5");
  const [category, setCategory] = useState("adventure");
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem("cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // V2 Enhanced Pipeline - only for v2/v3 (premium tiers)
  // v1 uses standard pipeline to keep costs down for free users
  // Calculated after selectedVersion and getDefaultVersion are available

  // Active job state for preview panel (Suno-style)
  const [activeJob, setActiveJob] = useState(null);
  const [showPreviewPanel, setShowPreviewPanel] = useState(false);
  const [workspaceRefreshKey, setWorkspaceRefreshKey] = useState(0);
  const eventSourceRef = useRef(null);

  // Simple/Custom Mode tabs (Suno-style)
  const [createMode, setCreateMode] = useState('simple'); // 'simple' | 'custom'

  // Story version selection
  // v1 = Standard (Flux), v2 = Premium (Gemini 2.5), v3 = Pro (Gemini 3)
  const [selectedVersion, setSelectedVersion] = useState(null); // null = auto-determine based on tier

  // Animation opt-in for premium users (+1 credit)
  const [enableAnimations, setEnableAnimations] = useState(false);

  // Narrator voice selection for premium users
  // nova (default female), onyx (male), echo (male), shimmer (female), alloy (neutral), fable (British)
  const [narratorVoice, setNarratorVoice] = useState("nova");

  // Legacy Pro Story Mode - now controlled by selectedVersion === 3
  const isProStory = selectedVersion === 3;

  // Expandable sections in Custom mode
  const [expandedSections, setExpandedSections] = useState({
    characters: true,
    story: false,
    style: false
  });

  // Enlarged photo modal
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);

  // My Characters feature - saved character library
  const [showSavedCharactersModal, setShowSavedCharactersModal] = useState(false);
  const [showSaveCharacterModal, setShowSaveCharacterModal] = useState(false);
  const [pendingSaveCharacter, setPendingSaveCharacter] = useState(null); // { index, name, portraitUrl, artStyle, colorPalette }

  // Calculate credit cost for story based on version and options
  // v1 = 1 credit, v2 = 1 credit, v3 = 2 credits
  // Animations add +1 credit
  const getStoryCost = () => {
    let cost = 1;
    if (selectedVersion === 3) {
      cost = 2;
    }
    if (enableAnimations) {
      cost += 1;
    }
    return cost;
  };

  // Check if user is a free user (no premium, no purchased credits)
  // Free users get restricted options to reduce costs
  const isFreeUser = !user?.is_premium && !user?.has_purchased_credits;

  // Get available versions based on user tier
  // Free users (even with starter credits): v1 only
  // Users who have purchased credits OR have subscription: v1 or v2
  // Premium subscribed users: v1, v2, or v3
  const getAvailableVersions = () => {
    if (user?.is_premium) {
      return [1, 2, 3]; // Premium can use all
    } else if (user?.has_purchased_credits) {
      return [1, 2]; // Users who have purchased can use v1 or v2
    }
    return [1]; // Free users only v1
  };

  // Get default version based on user tier
  const getDefaultVersion = () => {
    if (user?.is_premium) return 2;
    if (user?.has_purchased_credits) return 2;
    return 1;
  };

  // V2 Enhanced Pipeline - only for v2/v3 to keep v1 cost-efficient for free users
  // URL param ?v2=true can force V2 pipeline for testing
  const effectiveVersion = selectedVersion || getDefaultVersion();
  const forceV2 = searchParams.get('v2') === 'true';
  const useV2Pipeline = forceV2 || effectiveVersion >= 2;

  // Age-appropriate placeholder prompts
  const placeholderPrompts = {
    "3-5": "A brave little fox who crosses a wobbly bridge, climbs a tall hill, and splashes through a stream to deliver a birthday cupcake to his best friend...",
    "5-8": "Two siblings who find a hidden map in their grandfather's attic that leads to a secret cave behind the waterfall in the woods near their house...",
    "8-12": "A kid who gets lost during a family camping trip and must use survival skills and a mysterious compass to find their way back before nightfall..."
  };

  // Dynamic story prompt generator - Combinatorial system for maximum variety
  // Each component is mixed randomly to create thousands of unique combinations

  const storyBuilders = {
    // ========== AGES 3-5: Simple animal adventures ==========
    "3-5": {
      animals: [
        "pig", "owl", "puppy", "kitten", "caterpillar", "duck", "snail", "cat", "bear cub",
        "penguin", "frog", "sheep", "mouse", "elephant", "robin", "hamster", "giraffe",
        "worm", "bat", "chicken", "badger", "bunny", "turtle", "hedgehog", "squirrel",
        "ladybug", "butterfly", "bee", "otter", "fox", "raccoon", "deer", "chipmunk",
        "panda", "koala", "hippo", "zebra", "monkey", "parrot", "flamingo", "whale"
      ],
      names: [
        "Penny", "Oliver", "Charlie", "Lucy", "Max", "Lily", "Sam", "Rosie",
        "Ben", "Sophie", "Jack", "Daisy", "Henry", "Ruby", "Leo", "Ella",
        "Theo", "Molly", "Oscar", "Poppy", "Archie", "Chloe", "Alfie", "Mia",
        "George", "Grace", "Harry", "Emma", "Freddy", "Anna", "Tommy", "Katie",
        "Billy", "Zoe", "Bobby", "Ellie", "Teddy", "Bella", "Louie", "Holly"
      ],
      adjectives: [
        "little", "tiny", "fuzzy", "fluffy", "round", "spotted", "striped", "plump",
        "wobbly", "curious", "cheerful", "shy", "bouncy", "sleepy", "giggly", "wiggly",
        "gentle", "brave", "silly", "cozy", "sparkly", "cuddly", "clumsy", "chatty"
      ],
      quirks: [
        "who always wears rain boots", "who can't hoot properly", "who chases their own tail",
        "who's afraid of heights", "who waddles backwards", "who thinks they're fast",
        "who hates getting wet", "who loves honey too much", "who can't stand still",
        "who can't ribbit", "who can't sleep at night", "who collects shiny buttons",
        "who's scared of tiny bugs", "who sings off-key", "who stuffs their cheeks",
        "who has a very short neck", "who loves rainy days", "who's afraid of the dark",
        "who can't lay eggs", "who secretly likes hugs", "who sneezes when excited",
        "who hiccups when nervous", "who trips over everything", "who talks too loud",
        "who whispers all the time", "who laughs at their own jokes", "who loves mud puddles",
        "who is always hungry", "who forgets where they put things", "who asks too many questions"
      ],
      situations: [
        "discovers their favorite puddle leads to a world of tiny boats",
        "finds a door in the garden hedge that wasn't there yesterday",
        "wakes up to find all the colors in the house have swapped places",
        "follows a trail of cookie crumbs to a very unexpected place",
        "learns that their shadow wants to play different games today",
        "discovers the moon has been leaving them little presents",
        "finds out the garden snails are having a racing championship",
        "realizes the bathtub boats go on adventures when nobody's looking",
        "discovers their teddy bear has been organizing the other toys",
        "finds a map inside a library book that leads to buried treasure",
        "learns the fireflies are trying to spell out a message",
        "discovers the clouds are actually fluffy sheep on their way somewhere",
        "finds out the kitchen utensils have strong opinions about dinner",
        "wakes up to find a very small door has appeared in the wall",
        "discovers their rubber duck knows the secrets of the deep",
        "finds out the autumn leaves are having a going-away party",
        "learns that dandelion wishes really do go somewhere special",
        "discovers the raindrops are racing each other down the window",
        "finds a note in a bottle that washed up in the birdbath",
        "realizes the wind has been trying to tell everyone a story",
        "discovers their crayons have been drawing pictures at night",
        "finds out the backyard bugs have built an entire tiny city",
        "learns the stars have been playing connect-the-dots all along",
        "discovers their snow friend has been having moonlit adventures",
        "finds out what really happens to the socks that disappear",
        "learns that breakfast cereal has very strong feelings about milk",
        "discovers the treehouse has been hosting secret meetings",
        "finds a seed that grows into something completely unexpected",
        "learns why the crows always gather on Tuesday afternoons",
        "discovers their music box plays different songs when nobody's listening"
      ],
      endings: [
        "and learns that trying your best is what really matters",
        "and discovers that friends come in all shapes and sizes",
        "and finds out the best surprises come when you least expect them",
        "and realizes that being different is actually pretty wonderful",
        "and learns that sometimes the smallest things make the biggest difference",
        "and discovers that home is wherever your family is",
        "and finds that sharing makes everything taste better",
        "and learns that a good laugh can fix almost anything",
        "and realizes the best adventures are the ones close to home",
        "and discovers that mistakes can lead to wonderful things",
        "and learns that it's okay to ask for help",
        "and finds out that patience really is a superpower",
        "and realizes that kindness is the best gift of all"
      ]
    },

    // ========== AGES 5-8: Magical kid adventures ==========
    "5-8": {
      kidTypes: [
        "a kid", "a boy", "a girl", "twins", "best friends", "two siblings",
        "three cousins", "a young inventor", "a curious reader", "a shy artist"
      ],
      names: [
        "Max", "Emma", "Jack", "Lily", "Sam", "Sophie", "Ben", "Chloe", "Jake", "Grace",
        "Noah", "Ella", "Lucas", "Mia", "Henry", "Ruby", "Owen", "Zoe", "Charlie", "Anna",
        "Leo", "Katie", "Finn", "Lucy", "Ethan", "Molly", "Oliver", "Abby", "James", "Emily",
        "Ryan", "Hannah", "Tyler", "Sarah", "Alex", "Maddie", "Josh", "Ellie", "Dylan", "Ava"
      ],
      magicalTraits: [
        "whose drawings come alive at night",
        "whose shadow has a mind of its own",
        "who can hear what animals are thinking",
        "whose lucky penny grants tiny wishes",
        "who found a door in their closet to somewhere else",
        "whose grandmother's attic is full of magical secrets",
        "who befriended the creature under their bed",
        "whose library card works at a very unusual library",
        "who discovered their bike can fly (but only sometimes)",
        "who can fold paper into things that move",
        "whose goldfish talks back (and gives terrible advice)",
        "who bakes cookies that make people tell the truth",
        "whose music box opens portals to strange places",
        "who can see things that are about to happen",
        "whose toys come alive when nobody's watching",
        "who found glasses that let them see invisible things",
        "whose dreams keep coming true in weird ways",
        "who discovered they can shrink to the size of an ant",
        "whose pet rock is actually a sleeping dragon egg",
        "who can talk to plants (and the plants talk back)",
        "whose sneakers let them walk on walls",
        "who found a watch that can pause time for ten seconds",
        "whose backpack has a portal at the bottom",
        "who discovered their treehouse moves to different places at night",
        "whose camera captures moments that haven't happened yet"
      ],
      situations: [
        "must return a borrowed book before the library closes forever",
        "accidentally swaps voices with their best friend for a whole week",
        "discovers the new kid at school is from a very unusual place",
        "finds a treasure map inside an old library book",
        "must solve three riddles before sunset",
        "agrees to pet-sit a creature that keeps changing shape",
        "follows a trail of glowing footprints into the woods",
        "wakes up to find their bedroom has become a ship at sea",
        "must organize the strangest parade their town has ever seen",
        "gets stuck in a snow globe and must figure out how to escape",
        "discovers their boring neighbor is actually quite extraordinary",
        "accidentally enters a contest and somehow keeps winning",
        "finds out their tree fort has been claimed by someone else",
        "must deliver an important message but keeps getting sidetracked",
        "trades chores with a friend and realizes their life isn't so bad",
        "finds a key that doesn't fit any lock they've tried",
        "discovers their reflection is trying to tell them something",
        "must babysit their younger sibling during a very strange storm",
        "accidentally lets something out of an old music box",
        "finds out their stuffed animals have been having adventures without them",
        "discovers a secret room in their school that only appears on Tuesdays",
        "must return a star that fell into their backyard",
        "finds out the town's statues have been moving at night",
        "accidentally becomes the mayor of a tiny hidden village",
        "discovers a recipe book where every dish grants a different power"
      ],
      twists: [
        "but everything goes wonderfully, hilariously wrong",
        "and nothing is quite what it seems",
        "only to discover the real adventure was just beginning",
        "and meets three very unusual helpers along the way",
        "but first must learn to trust an unlikely friend",
        "and finds that the solution was inside them all along",
        "only to realize they had it backwards the whole time",
        "and discovers that sometimes getting lost leads you exactly where you need to be",
        "while learning that magic comes with unexpected rules",
        "and realizes that the greatest treasure isn't what they expected",
        "but discovers that some secrets are meant to be shared",
        "and learns that courage isn't about not being scared"
      ]
    },

    // ========== AGES 8-12: Mystery and adventure ==========
    "8-12": {
      protagonists: [
        "A twelve-year-old", "A seventh-grader", "An only child", "The new kid in town",
        "A reluctant babysitter", "An amateur detective", "A skeptical scientist",
        "The class outcast", "A young chess champion", "An aspiring journalist"
      ],
      names: [
        "Alex", "Emma", "Jake", "Sophie", "Ryan", "Katie", "Matt", "Lily", "Josh", "Grace",
        "Ben", "Chloe", "Sam", "Mia", "Chris", "Hannah", "Nick", "Ella", "Tyler", "Anna",
        "Jack", "Zoe", "Luke", "Lucy", "Mike", "Sarah", "Dan", "Emily", "Tom", "Abby",
        "Max", "Molly", "Will", "Rachel", "Evan", "Julia", "Cole", "Maddie", "Drew", "Leah"
      ],
      discoveries: [
        "who just inherited a very strange antique shop",
        "whose family has kept an unusual secret for generations",
        "who discovers their summer camp has an underground level",
        "whose new phone receives texts from the future",
        "whose reflection started acting independently",
        "who finds their grandparent's journal full of impossible things",
        "whose new neighbor only comes outside at night",
        "who accidentally becomes the captain of a ghost ship",
        "whose chess pieces have started giving cryptic advice",
        "who discovers their small town isn't on any map",
        "who can suddenly understand every language (even dead ones)",
        "whose shadow started collecting shadows of its own",
        "whose summer job is at a hotel for unusual guests",
        "who finds out their boring family reunion hides a centuries-old tradition",
        "who discovers their house was built on top of something ancient",
        "whose podcast accidentally contacts something that answers back",
        "who finds a locker at school that belongs to a student who never existed",
        "whose new glasses let them see things others can't",
        "who receives a scholarship to a school they never applied to",
        "who finds their name written in a book from 200 years ago",
        "whose video game character started leaving messages for them",
        "who discovers the local urban legends are all true",
        "who inherits a map to a place that keeps moving",
        "whose imaginary friend from childhood shows up as a real person",
        "who finds out their boring suburban town has a secret underground"
      ],
      situations: [
        "must solve a mystery their missing uncle left behind",
        "discovers an old rivalry between two families that changes everything",
        "finds out the town's founding story is completely wrong",
        "must choose between keeping a secret or saving a friend",
        "accidentally breaks a rule nobody told them about",
        "uncovers a hidden room that shouldn't exist",
        "realizes the weird events happening aren't coincidences",
        "must earn the trust of someone who has every reason not to give it",
        "discovers they're the only one who remembers how things used to be",
        "finds a letter addressed to them from fifty years ago",
        "must complete a task their ancestor started but never finished",
        "realizes the strange dreams they've been having are actually memories",
        "discovers their greatest weakness might actually be their greatest strength",
        "finds out they've been chosen for something they never wanted",
        "realizes one of their friends has been hiding a huge secret",
        "discovers the adults in town have been lying about something important",
        "must decode a message before someone else figures it out first",
        "finds evidence that history books got something very wrong",
        "realizes they're being followed by someone (or something)",
        "discovers a countdown that nobody else can see"
      ],
      stakes: [
        "and the clock is ticking on a deadline nobody else knows about",
        "while keeping up appearances at school like nothing's wrong",
        "and must decide what they're willing to give up to set things right",
        "but the more they learn, the more dangerous it becomes",
        "and discovers that some secrets are kept for very good reasons",
        "while figuring out who they can actually trust",
        "and learns that growing up means making impossible choices",
        "but first must confront the one thing they've been avoiding",
        "and realizes that being the hero isn't as simple as it sounds",
        "while questioning everything they thought they knew",
        "and discovers that the truth might be scarier than the mystery",
        "but learns that asking for help isn't a sign of weakness"
      ]
    }
  };

  // Helper to pick random item from array
  const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

  // Generate a dynamic story prompt using combinatorial building blocks
  // Ages 3-5: ~40 animals × 48 names × 24 adjectives × 30 quirks × 30 situations = millions of combinations
  // Ages 5-8: ~10 types × 50 names × 25 traits × 25 situations × 12 twists = millions of combinations
  // Ages 8-12: ~10 protagonists × 40 names × 25 discoveries × 20 situations × 12 stakes = millions of combinations
  const generateStoryPrompt = (age) => {
    const builders = storyBuilders[age] || storyBuilders["3-5"];

    if (age === "3-5") {
      // Build: "A [adjective] [animal] named [name] [quirk] [situation] [ending?]"
      const adjective = pickRandom(builders.adjectives);
      const animal = pickRandom(builders.animals);
      const name = pickRandom(builders.names);
      const quirk = pickRandom(builders.quirks);
      const situation = pickRandom(builders.situations);
      const ending = pickRandom(builders.endings);

      // 50% chance to include quirk, 40% chance to include ending
      const includeQuirk = Math.random() > 0.5;
      const includeEnding = Math.random() > 0.6;

      let prompt = `A ${adjective} ${animal} named ${name}`;
      if (includeQuirk) prompt += ` ${quirk}`;
      prompt += ` ${situation}`;
      if (includeEnding) prompt += ` ${ending}`;

      return prompt;

    } else if (age === "5-8") {
      // Build: "[kidType] named [name] [magicalTrait] [situation] [twist?]"
      const kidType = pickRandom(builders.kidTypes);
      const name = pickRandom(builders.names);
      const magicalTrait = pickRandom(builders.magicalTraits);
      const situation = pickRandom(builders.situations);
      const twist = pickRandom(builders.twists);

      // 60% chance to include the twist
      const includeTwist = Math.random() > 0.4;

      // Handle plural kid types (twins, siblings, etc.)
      const isPlural = kidType.includes("twins") || kidType.includes("siblings") ||
                       kidType.includes("friends") || kidType.includes("cousins");
      const name2 = pickRandom(builders.names.filter(n => n !== name));

      let prompt;
      if (isPlural) {
        prompt = `${kidType.charAt(0).toUpperCase() + kidType.slice(1)} named ${name} and ${name2}`;
        // For plural, pick a trait that works or skip it
        if (Math.random() > 0.3) {
          prompt += ` who discover something magical`;
        }
      } else {
        prompt = `${kidType.charAt(0).toUpperCase() + kidType.slice(1)} named ${name} ${magicalTrait}`;
      }

      prompt += ` ${situation}`;
      if (includeTwist) prompt += ` ${twist}`;

      return prompt;

    } else {
      // Build: "[protagonist] named [name] [discovery] [situation] [stakes?]"
      const protagonist = pickRandom(builders.protagonists);
      const name = pickRandom(builders.names);
      const discovery = pickRandom(builders.discoveries);
      const situation = pickRandom(builders.situations);
      const stakes = pickRandom(builders.stakes);

      // 70% chance to include stakes
      const includeStakes = Math.random() > 0.3;

      let prompt = `${protagonist} named ${name} ${discovery} ${situation}`;
      if (includeStakes) prompt += ` ${stakes}`;

      return prompt;
    }
  };

  // State for random prompt loading
  const [isRandomizing, setIsRandomizing] = useState(false);

  // Handle random prompt selection - calls backend API for AI-generated prompts
  const handleRandomize = async () => {
    setIsRandomizing(true);
    try {
      const response = await axios.post(`${API}/v1/prompts/random`, {
        age_range: ageRange
      });
      setPrompt(response.data.prompt);
    } catch (error) {
      console.error("Failed to generate random prompt:", error);
      // Fallback to local generation if API fails
      setPrompt(generateStoryPrompt(ageRange));
    } finally {
      setIsRandomizing(false);
    }
  };

  // Age-based story length options (page counts)
  const storyLengthOptions = {
    "3-5": [
      { value: "8", label: "8 pages", pages: 8 },
      { value: "10", label: "10 pages", pages: 10 },
      { value: "12", label: "12 pages", pages: 12 },
      { value: "14", label: "14 pages", pages: 14 }
    ],
    "5-8": [
      { value: "10", label: "10 pages", pages: 10 },
      { value: "12", label: "12 pages", pages: 12 },
      { value: "14", label: "14 pages", pages: 14 },
      { value: "16", label: "16 pages", pages: 16 }
    ],
    "8-12": [
      { value: "20", label: "20 pages", pages: 20 },
      { value: "30", label: "30 pages", pages: 30 },
      { value: "40", label: "40 pages", pages: 40 }
    ]
  };

  // Default page counts: 12 for picture books/easy readers, 20 for chapter books
  const defaultStoryLength = {
    "3-5": "12",
    "5-8": "12",
    "8-12": "20"
  };

  // Minimum page counts for free users (first option in each age range)
  const freeUserStoryLength = {
    "3-5": "8",
    "5-8": "10",
    "8-12": "20"
  };

  // Get current length options based on age
  const currentLengthOptions = storyLengthOptions[ageRange] || storyLengthOptions["3-5"];

  // Get page count from selected story length
  const getPageCount = () => {
    const option = currentLengthOptions.find(o => o.value === storyLength);
    return option ? option.pages : currentLengthOptions[0].pages;
  };

  // Character customization (up to 4)
  const [customCharacters, setCustomCharacters] = useState([
    { name: "", description: "", uploaded_photo_url: null, portrait_description: null, photoPreview: null, isUploading: false, pendingPhoto: null, pendingPreview: null }
  ]);

  // Story structure customization
  const [customSetting, setCustomSetting] = useState("");
  const [customTheme, setCustomTheme] = useState("");
  const [customMoral, setCustomMoral] = useState("");

  // Narrative customization
  const [narrativeStyle, setNarrativeStyle] = useState("prose");
  const [narrativeTone, setNarrativeTone] = useState("warm");
  const [storyLength, setStoryLength] = useState("12"); // Default to 12 pages
  const [includeDialogue, setIncludeDialogue] = useState(true);

  // Visual customization
  const [colorPalette, setColorPalette] = useState("warm_pastels");
  const [numPages, setNumPages] = useState(12);

  // Visual Presets (primary style control - includes merged realism)
  const [visualPreset, setVisualPreset] = useState("classic-storybook");

  // Use shared categories from constants
  const allCategories = STORY_CATEGORIES;

  useEffect(() => {
    checkAuth();
  }, []);


  // Set default version when user loads/changes
  useEffect(() => {
    if (user && selectedVersion === null) {
      setSelectedVersion(getDefaultVersion());
    }
  }, [user]);

  // Enforce restrictions based on user tier
  // Custom mode requires premium subscription (not just purchased credits)
  // Free users get minimum page count for their chosen age range
  useEffect(() => {
    // Custom mode requires premium subscription
    if (!user?.is_premium && createMode === 'custom') {
      setCreateMode("simple");
    }
    // Free users are locked to minimum page count for their age range
    if (isFreeUser) {
      setStoryLength(freeUserStoryLength[ageRange] || "8");
    }
  }, [user?.is_premium, isFreeUser, createMode, ageRange]);

  // Set appropriate default story length when age range changes (for non-free users)
  useEffect(() => {
    if (!isFreeUser && defaultStoryLength[ageRange]) {
      setStoryLength(defaultStoryLength[ageRange]);
    }
  }, [ageRange, isFreeUser]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setCheckingAuth(false);
    }
  };

  // Handle successful auth from modal
  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    checkAuth();
  };

  // Character management helpers
  const addCharacter = () => {
    if (customCharacters.length < 4) {
      setCustomCharacters([...customCharacters, { name: "", description: "", uploaded_photo_url: null, portrait_description: null, photoPreview: null, isUploading: false, pendingPhoto: null, pendingPreview: null }]);
    }
  };

  const removeCharacter = (index) => {
    if (customCharacters.length > 1) {
      setCustomCharacters(customCharacters.filter((_, i) => i !== index));
    }
  };

  const updateCharacter = (index, field, value) => {
    const updated = [...customCharacters];
    updated[index][field] = value;
    setCustomCharacters(updated);
  };

  // Handle photo selection for character (preview only, no upload yet)
  const handlePhotoSelect = (index, file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type', { description: 'Please upload a JPEG, PNG, or WebP image.' });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large', { description: 'Maximum file size is 10MB.' });
      return;
    }

    // Create preview and store pending file (don't upload yet)
    const previewUrl = URL.createObjectURL(file);
    const updated = [...customCharacters];
    updated[index].pendingPhoto = file;
    updated[index].pendingPreview = previewUrl;
    setCustomCharacters(updated);
  };

  // Confirm and upload the pending photo for transformation
  const confirmPhotoUpload = async (index) => {
    const char = customCharacters[index];
    if (!char.pendingPhoto) return;

    // Save references before clearing state
    const fileToUpload = char.pendingPhoto;
    const characterName = char.name || `Character ${index + 1}`;
    const previewUrl = char.pendingPreview;

    // Move pending to active upload state (use functional update to avoid stale state)
    setCustomCharacters(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        isUploading: true,
        photoPreview: previewUrl,
        pendingPhoto: null,
        pendingPreview: null
      };
      return updated;
    });

    try {
      // Derive art_style from visual_preset for character photo transformation
      const presetToArtStyle = {
        'classic-storybook': 'storybook',
        'soft-watercolor': 'watercolor',
        'comic-adventure': 'cartoon',
        'modern-minimalist': 'cartoon',
        'dreamy-pastel': 'watercolor',
        'realistic-painted': 'watercolor',
        'cinematic-3d': '3d-render',
        'biblical-epic': 'storybook',
        // Classic children's book styles
        'classic-realistic': 'classic-realistic',
        'gentle-pastoral': 'gentle-pastoral',
        'retro-midcentury': 'retro-midcentury',
      };
      const derivedArtStyle = presetToArtStyle[visualPreset] || 'storybook';

      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('character_name', characterName);
      formData.append('art_style', derivedArtStyle);
      formData.append('color_palette', colorPalette);

      const response = await axios.post(`${API}/v1/uploads/character-photo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update character with transformed URL and portrait description (functional update preserves other characters' state)
      const transformedUrl = response.data.transformed_url;
      const portraitDescription = response.data.portrait_description;
      setCustomCharacters(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          uploaded_photo_url: transformedUrl,
          portrait_description: portraitDescription,
          isUploading: false
        };
        return updated;
      });

      toast.success('Photo transformed!', { description: `${response.data.character_name} is ready for the story.` });

      // Show save to library modal for premium users
      if (user?.is_premium) {
        setPendingSaveCharacter({
          index,
          name: characterName,
          portraitUrl: transformedUrl,
          artStyle: derivedArtStyle,
          colorPalette: colorPalette
        });
        setShowSaveCharacterModal(true);
      }
    } catch (error) {
      console.error('Photo upload failed:', error);
      const message = error.response?.data?.detail || 'Failed to transform photo';
      toast.error('Upload failed', { description: message });

      // Reset upload state (functional update preserves other characters' state)
      setCustomCharacters(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          isUploading: false,
          photoPreview: null
        };
        return updated;
      });
    }
  };

  // Cancel pending photo without uploading
  const cancelPendingPhoto = (index) => {
    const char = customCharacters[index];
    if (char.pendingPreview) {
      URL.revokeObjectURL(char.pendingPreview);
    }
    const updated = [...customCharacters];
    updated[index].pendingPhoto = null;
    updated[index].pendingPreview = null;
    setCustomCharacters(updated);
  };

  // Remove uploaded photo from character
  const removeCharacterPhoto = (index) => {
    const updated = [...customCharacters];
    updated[index].uploaded_photo_url = null;
    updated[index].photoPreview = null;
    setCustomCharacters(updated);
  };

  // Handle selecting a saved character from the library
  const handleSelectSavedCharacter = (savedCharacter) => {
    // Find the first empty character slot, or add a new one if space available
    let targetIndex = customCharacters.findIndex(c => !c.name.trim() && !c.uploaded_photo_url);

    if (targetIndex === -1) {
      // No empty slot - add a new character if we have room
      if (customCharacters.length < 4) {
        targetIndex = customCharacters.length;
        setCustomCharacters(prev => [
          ...prev,
          {
            name: savedCharacter.name,
            description: savedCharacter.description || "",
            uploaded_photo_url: savedCharacter.portrait_url,
            portrait_description: savedCharacter.portrait_description || null,
            photoPreview: null,
            isUploading: false,
            pendingPhoto: null,
            pendingPreview: null
          }
        ]);
        toast.success('Character added!', { description: `${savedCharacter.name} has been added to your story.` });
        return;
      } else {
        // All slots full - replace the first one
        targetIndex = 0;
        toast.info('Character replaced', { description: `Replaced first character with ${savedCharacter.name}.` });
      }
    }

    // Update the target slot with the saved character
    setCustomCharacters(prev => {
      const updated = [...prev];
      updated[targetIndex] = {
        ...updated[targetIndex],
        name: savedCharacter.name,
        description: savedCharacter.description || "",
        uploaded_photo_url: savedCharacter.portrait_url,
        portrait_description: savedCharacter.portrait_description || null,
        photoPreview: null,
        isUploading: false,
        pendingPhoto: null,
        pendingPreview: null
      };
      return updated;
    });

    toast.success('Character added!', { description: `${savedCharacter.name} has been added to your story.` });
  };

  // Handle closing the save character modal
  const handleSaveCharacterClose = () => {
    setShowSaveCharacterModal(false);
    setPendingSaveCharacter(null);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Handle switching between Simple and Custom mode
  const handleModeChange = (mode) => {
    setCreateMode(mode);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a story idea!");
      return;
    }

    setLoading(true);
    setProgress("Creating your story job...");
    setProgressPercent(10);

    try {
      const payload = {
        prompt: prompt.trim(),
        age_range: ageRange,
        category: category,
        is_public: isPublic,
        is_pro_story: isProStory,
        story_version: selectedVersion || getDefaultVersion(),
        advanced_mode: createMode === 'custom',
        use_v2_pipeline: useV2Pipeline,
        enable_animations: enableAnimations,
        narrator_voice: narratorVoice,
      };

      payload.story_length = storyLength;

      // Visual Presets (works in both Simple and Custom modes - includes merged realism)
      payload.visual_preset = visualPreset;

      // Derive art_style from visual_preset for backend compatibility
      const presetToArtStyle = {
        'classic-storybook': 'storybook',
        'soft-watercolor': 'watercolor',
        'comic-adventure': 'cartoon',
        'modern-minimalist': 'cartoon',
        'dreamy-pastel': 'watercolor',
        'realistic-painted': 'watercolor',
        'cinematic-3d': '3d-render',
        'biblical-epic': 'storybook',
        // Classic children's book styles
        'classic-realistic': 'classic-realistic',
        'gentle-pastoral': 'gentle-pastoral',
        'retro-midcentury': 'retro-midcentury',
      };
      payload.art_style = presetToArtStyle[visualPreset] || 'storybook';
      payload.color_palette = colorPalette;

      if (createMode === 'custom') {
        const validCharacters = customCharacters
          .filter(c => c.name.trim())
          .map(c => ({
            name: c.name.trim(),
            description: c.description?.trim() || "",
            uploaded_photo_url: c.uploaded_photo_url || null,
            portrait_description: c.portrait_description || null
          }));
        if (validCharacters.length > 0) {
          payload.custom_characters = validCharacters;
        }
        if (customSetting.trim()) {
          payload.custom_setting = customSetting.trim();
        }
        if (customTheme.trim()) {
          payload.custom_theme = customTheme.trim();
        }
        if (customMoral.trim()) {
          payload.custom_moral = customMoral.trim();
        }
        payload.narrative_style = narrativeStyle;
        payload.narrative_tone = narrativeTone;
        payload.include_dialogue = includeDialogue;
        payload.num_pages = getPageCount();
      }

      const response = await axios.post(`${API}/v1/jobs`, payload, {
        withCredentials: true
      });

      const { job_id, queue_position } = response.data;

      if (queue_position > 1) {
        toast.info(`You're #${queue_position} in queue`, { duration: 3000 });
      } else {
        toast.success("Starting story generation!");
      }

      setActiveJob({
        id: job_id,
        status: 'queued',
        step: 'pending',
        progress: 0,
        message: 'Queued...',
        queue_position: queue_position,
        thumbnail_url: null,
        prompt: prompt.trim()
      });
      // Trigger MyStoriesWorkspace to refresh and pick up the new job
      setWorkspaceRefreshKey(prev => prev + 1);
      startJobStream(job_id);

      // Reset form for next story
      setPrompt("");
      setCustomCharacters([{ name: "", description: "", uploaded_photo_url: null, portrait_description: null, photoPreview: null, isUploading: false }]);
      setCustomSetting("");
      setCustomTheme("");
      setCustomMoral("");
      setSelectedVersion(getDefaultVersion()); // Reset to default version
      setVisualPreset("classic-storybook");
      setEnableAnimations(false); // Reset animation opt-in
    } catch (error) {
      console.error("Error creating story job:", error);

      if (error.response?.status === 401) {
        toast.error("Please login to create stories", {
          action: { label: "Login", onClick: () => navigate('/login') }
        });
      } else if (error.response?.status === 403) {
        const message = error.response?.data?.detail || "Credits required";
        if (message.includes("login")) {
          toast.error("Please login to create more stories", {
            action: { label: "Login", onClick: () => navigate('/login') }
          });
        } else {
          toast.error(message, {
            action: { label: "Add Credits", onClick: () => navigate('/library') }
          });
        }
      } else if (error.response?.status === 429) {
        toast.error("You already have a story being generated. Please wait for it to complete.", {
          action: { label: "View Progress", onClick: () => navigate('/library') }
        });
      } else {
        // Handle validation errors (422) which return detail as an array
        let errorMessage = "Failed to create story. Please try again.";
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          // Pydantic validation errors are arrays of {type, loc, msg, input, url}
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
      setProgress("");
      setProgressPercent(0);
    }
  };

  // Handle recreating a story - try again with same settings
  const handleRecreate = (story) => {
    setPrompt(story.prompt || '');
    if (story.age_range) setAgeRange(story.age_range);
    if (story.category) setCategory(story.category);
    // Keep in simple mode - user wants to try again
    setCreateMode('simple');
    toast.success("Ready to try again!", {
      description: "Same prompt loaded. Hit create to generate a new version."
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle creating a new version - same prompt but customize the style
  const handleReimagine = (story) => {
    setPrompt(story.prompt || '');
    if (story.age_range) setAgeRange(story.age_range);
    if (story.category) setCategory(story.category);
    // Switch to custom mode and expand style section
    setCreateMode('custom');
    setExpandedSections({ characters: false, story: false, style: true });
    toast.success("Create a new version!", {
      description: "Customize the story with new art styles or inspirations."
    });
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startJobStream = (jobId) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const streamUrl = `${API}/v1/jobs/${jobId}/stream`;
    const eventSource = new EventSource(streamUrl, { withCredentials: true });
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'complete') {
          toast.success('Your story is ready!');
          eventSource.close();
          navigate(`/story/${data.story_id}`, { replace: true });
        } else if (data.type === 'failed') {
          setActiveJob(prev => ({ ...prev, status: 'failed', error: data.error }));
          eventSource.close();
        } else if (data.type === 'status') {
          setActiveJob(prev => ({ ...prev, ...data }));
        }
      } catch (err) {
        console.error('Failed to parse SSE message:', err);
      }
    };

    eventSource.onerror = () => {
      eventSource.close();
    };

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/v1/jobs/${jobId}`, { withCredentials: true });
        const data = response.data;
        if (data.status === 'completed' && data.story_id) {
          clearInterval(pollInterval);
          eventSource.close();
          toast.success('Your story is ready!');
          navigate(`/story/${data.story_id}`, { replace: true });
        } else if (data.status === 'failed') {
          clearInterval(pollInterval);
          eventSource.close();
          setActiveJob(prev => ({ ...prev, status: 'failed', error: data.error }));
        } else {
          setActiveJob(prev => ({ ...prev, ...data }));
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);

    eventSourceRef.current._pollInterval = pollInterval;
  };

  const handleClosePreview = async () => {
    if (activeJob?.status === 'queued') {
      try {
        await axios.delete(`${API}/v1/jobs/${activeJob.id}`, { withCredentials: true });
        toast.success('Job cancelled, credit refunded');
      } catch (err) {
        console.error('Failed to cancel job:', err);
      }
    }

    if (eventSourceRef.current) {
      if (eventSourceRef.current._pollInterval) {
        clearInterval(eventSourceRef.current._pollInterval);
      }
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setActiveJob(null);
    setShowPreviewPanel(false);
    setLoading(false);
  };

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        if (eventSourceRef.current._pollInterval) {
          clearInterval(eventSourceRef.current._pollInterval);
        }
        eventSourceRef.current.close();
      }
    };
  }, []);


  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-2xl mx-auto px-4 py-4 md:py-6 md:px-8 2xl:px-12">

          {/* Page Header - Full Width */}
          <div className="rounded-2xl shadow-sm p-5 flex items-center justify-between mb-6" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                Storyboard
              </h1>
              <p className="text-sm mt-0.5" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                Create your next magical adventure
              </p>
            </div>
            <div className="flex items-center gap-2">
              {user?.is_premium && (
                <button
                  onClick={() => navigate('/series')}
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:bg-indigo-100"
                  style={{ background: '#EEF2FF', color: '#6366F1', fontFamily: 'Quicksand, sans-serif', border: '1px solid #E0E7FF' }}
                >
                  <Library className="w-4 h-4" />
                  Create in Series
                </button>
              )}
              <span className="text-sm font-medium px-3 py-1.5 rounded-lg" style={{ background: 'var(--st-border-light)', color: 'var(--st-text-muted)', fontFamily: 'Quicksand, sans-serif' }}>
                {user?.credits || 0} credits
              </span>
            </div>
          </div>

          <div className={`grid grid-cols-1 gap-6 ${showPreviewPanel ? 'lg:grid-cols-2' : 'lg:grid-cols-3'}`}>

            {/* Main Form - Left Column */}
            <div className={`${showPreviewPanel ? '' : 'lg:col-span-2'} space-y-4`}>

              {/* Header Row with Tabs */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'var(--st-bg-muted)' }}>
                  <button
                    onClick={() => handleModeChange('simple')}
                    className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      createMode === 'simple' ? 'bg-white dark:bg-white/10 shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
                    }`}
                    style={{ fontFamily: 'Quicksand, sans-serif', color: createMode === 'simple' ? 'var(--st-text-heading)' : 'var(--st-text-muted)' }}
                  >
                    Simple
                  </button>
                  <button
                    onClick={() => {
                      if (!user) {
                        // Not logged in - show auth modal
                        setShowAuthModal(true);
                        return;
                      }
                      if (!user.is_premium) {
                        toast.error("Custom mode requires a premium subscription");
                        navigate('/pricing');
                        return;
                      }
                      handleModeChange('custom');
                    }}
                    className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center gap-1.5 ${
                      createMode === 'custom' ? 'bg-white dark:bg-white/10 shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
                    } ${!user?.is_premium ? 'opacity-70' : ''}`}
                    style={{ fontFamily: 'Quicksand, sans-serif', color: createMode === 'custom' ? 'var(--st-text-heading)' : 'var(--st-text-muted)' }}
                  >
                    Custom
                    {!user?.is_premium && <Crown className="w-3.5 h-3.5" style={{ color: '#F59E0B' }} />}
                  </button>
                </div>
                {/* Version Dropdown */}
                <Select
                  value={selectedVersion?.toString() || getDefaultVersion().toString()}
                  onValueChange={(value) => {
                    const version = parseInt(value);
                    if (!getAvailableVersions().includes(version)) {
                      if (version === 2) {
                        toast.error("Purchase credits or subscribe to unlock v2");
                      } else if (version === 3) {
                        toast.error("Subscribe to unlock v3 Pro");
                      }
                      navigate('/pricing');
                      return;
                    }
                    if (version === 3) {
                      if (user?.credits < 2) {
                        toast.error("You need at least 2 credits for v3 Pro");
                        return;
                      }
                    }
                    setSelectedVersion(version);
                  }}
                  disabled={loading}
                >
                  <SelectTrigger
                    className="w-auto min-w-[70px] h-9 border-0 focus:ring-0"
                    style={{ background: 'var(--st-bg-muted)', fontFamily: 'Quicksand, sans-serif' }}
                  >
                    <SelectValue>
                      <div className="flex items-center gap-1">
                        {(selectedVersion || getDefaultVersion()) === 3 && (
                          <Star className="w-3 h-3" style={{ color: '#7C3AED' }} />
                        )}
                        <span
                          className="text-sm font-semibold"
                          style={{
                            color: (selectedVersion || getDefaultVersion()) === 1 ? '#6B7280'
                              : (selectedVersion || getDefaultVersion()) === 2 ? '#3B82F6'
                              : '#7C3AED'
                          }}
                        >
                          v{selectedVersion || getDefaultVersion()}
                        </span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {/* Show v1 for free users and admins (for testing) */}
                    {((!user?.is_premium && !user?.has_purchased_credits) || user?.is_admin) && (
                      <SelectItem value="1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">v1</span>
                          <span className="text-xs text-gray-500">Standard</span>
                        </div>
                      </SelectItem>
                    )}
                    <SelectItem value="2" disabled={!getAvailableVersions().includes(2)}>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold" style={{ color: getAvailableVersions().includes(2) ? '#3B82F6' : '#9CA3AF' }}>v2</span>
                        <span className="text-xs" style={{ color: getAvailableVersions().includes(2) ? '#3B82F6' : '#9CA3AF' }}>Premium</span>
                        {!getAvailableVersions().includes(2) && <Lock className="w-3 h-3 text-gray-400" />}
                      </div>
                    </SelectItem>
                    <SelectItem value="3" disabled={!getAvailableVersions().includes(3)}>
                      <div className="flex items-center gap-2">
                        <Star className="w-3 h-3" style={{ color: getAvailableVersions().includes(3) ? '#7C3AED' : '#9CA3AF' }} />
                        <span className="font-semibold" style={{ color: getAvailableVersions().includes(3) ? '#7C3AED' : '#9CA3AF' }}>v3</span>
                        <span className="text-xs" style={{ color: getAvailableVersions().includes(3) ? '#7C3AED' : '#9CA3AF' }}>Pro</span>
                        {!getAvailableVersions().includes(3) && <Lock className="w-3 h-3 text-gray-400" />}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Free User Info Banner */}
              {isFreeUser && (
                <div
                  className="flex items-center gap-2 p-3 rounded-xl mb-3 text-xs"
                  style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E40AF' }}
                >
                  <BookOpen className="w-4 h-4 flex-shrink-0" />
                  <span>
                    <strong>Free tier:</strong> Standard page counts ({freeUserStoryLength[ageRange]} pages). <button onClick={() => navigate('/pricing')} className="underline hover:no-underline">Upgrade</button> for longer stories and premium features.
                  </span>
                </div>
              )}

              {/* Main Card - Story Input */}
              <div className="rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                {/* Story Idea */}
                <div className="p-4 sm:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)' }}>
                        <Wand2 className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-bold text-sm sm:text-base" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                        What's your story about?
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Public Toggle */}
                      <button
                        onClick={() => setIsPublic(!isPublic)}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                          isPublic
                            ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}
                        disabled={loading}
                      >
                        {isPublic ? <Globe className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                        {isPublic ? 'Public' : 'Private'}
                      </button>

                      {/* Random Prompt Button */}
                      <button
                        onClick={handleRandomize}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50 disabled:opacity-50"
                        disabled={loading || isRandomizing}
                        title="Get a random story idea"
                      >
                        {isRandomizing ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Dices className="w-3.5 h-3.5" />
                        )}
                        {isRandomizing ? 'Thinking...' : 'Random'}
                      </button>

                      <span className="text-xs ml-1" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        {prompt.length}/500
                      </span>
                    </div>
                  </div>
                  <Textarea
                    data-testid="story-prompt-input"
                    placeholder={placeholderPrompts[ageRange] || placeholderPrompts["3-5"]}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value.slice(0, 500))}
                    className="min-h-[120px] text-base border-0 focus:ring-0 rounded-xl resize-none p-4"
                    disabled={loading}
                    style={{
                      fontFamily: 'Quicksand, sans-serif',
                      background: 'var(--st-bg-input)',
                      fontSize: '15px',
                      lineHeight: '1.6'
                    }}
                  />
                </div>

                {/* Quick Settings - Inline */}
                <div className="px-4 sm:px-5 pb-4 sm:pb-5">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Age Range */}
                    <Select value={ageRange} onValueChange={setAgeRange} disabled={loading}>
                      <SelectTrigger className="w-auto border-0 h-8 px-3 text-xs sm:text-sm font-medium rounded-lg gap-1" style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}>
                        <BookOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--st-text-muted)' }} />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3-5">Picture Book</SelectItem>
                        <SelectItem value="5-8">Easy Reader</SelectItem>
                        <SelectItem value="8-12">Chapter Book</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Category */}
                    <Select value={category} onValueChange={setCategory} disabled={loading}>
                      <SelectTrigger className="w-auto border-0 h-8 px-3 text-xs sm:text-sm font-medium rounded-lg" style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}>
                        <SelectValue>{allCategories.find(c => c.value === category)?.label || "Adventure"}</SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {allCategories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Visual Preset */}
                    <Select value={visualPreset} onValueChange={setVisualPreset} disabled={loading}>
                      <SelectTrigger className="w-auto border-0 h-8 px-3 text-xs sm:text-sm font-medium rounded-lg gap-1" style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}>
                        <Palette className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--st-text-muted)' }} />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="classic-storybook">Classic Storybook</SelectItem>
                        <SelectItem value="soft-watercolor">Soft Watercolor</SelectItem>
                        <SelectItem value="comic-adventure">Comic Adventure</SelectItem>
                        <SelectItem value="modern-minimalist">Modern Minimalist</SelectItem>
                        <SelectItem value="dreamy-pastel">Dreamy Pastel</SelectItem>
                        <SelectItem value="realistic-painted">Realistic Painted</SelectItem>
                        <SelectItem value="cinematic-3d">Cinematic 3D</SelectItem>
                        <SelectItem value="biblical-epic">Biblical Epic</SelectItem>
                        {/* Classic Children's Book Styles */}
                        <SelectItem value="classic-realistic">Classic Realistic</SelectItem>
                        <SelectItem value="gentle-pastoral">Gentle Pastoral</SelectItem>
                        <SelectItem value="retro-midcentury">Retro Mid-Century</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Custom Mode Options */}
              {createMode === 'custom' && (
                <div className="rounded-2xl shadow-sm overflow-hidden animate-in slide-in-from-top-2 duration-300" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>

                  {/* Characters Section */}
                  <CollapsibleSection
                    title="Characters"
                    icon={Users}
                    expanded={expandedSections.characters}
                    onToggle={() => toggleSection('characters')}
                    badge={customCharacters.filter(c => c.name.trim()).length > 0 ? `${customCharacters.filter(c => c.name.trim()).length}/4` : null}
                  >
                    <div className="space-y-3">
                      {customCharacters.map((char, index) => (
                        <div key={index} className="p-3 rounded-xl" style={{ background: 'var(--st-bg-muted)', border: '1px solid var(--st-border-medium)' }}>
                          <div className="flex gap-2 mb-2">
                            <Input
                              placeholder="Name"
                              value={char.name}
                              onChange={(e) => updateCharacter(index, 'name', e.target.value)}
                              disabled={loading || char.isUploading}
                              className="flex-1 border rounded-lg h-10 text-sm"
                              style={{ borderColor: 'var(--st-border-medium)', background: 'var(--st-bg-surface)' }}
                            />
                            <Input
                              placeholder="Description (optional)"
                              value={char.description}
                              onChange={(e) => updateCharacter(index, 'description', e.target.value)}
                              disabled={loading || char.isUploading}
                              className="flex-[2] border rounded-lg h-10 text-sm"
                              style={{ borderColor: 'var(--st-border-medium)', background: 'var(--st-bg-surface)' }}
                            />
                            {customCharacters.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeCharacter(index)}
                                disabled={char.isUploading}
                                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {/* Photo Upload Section - Premium Only */}
                          {user?.is_premium && (
                            <div className="mt-2">
                              {char.uploaded_photo_url ? (
                                // Show transformed photo
                                <div className="flex items-center gap-3">
                                  <div
                                    className="relative cursor-pointer group"
                                    onClick={() => setEnlargedPhoto(char.uploaded_photo_url.startsWith('/') ? BACKEND_URL + char.uploaded_photo_url : char.uploaded_photo_url)}
                                  >
                                    <img
                                      src={char.uploaded_photo_url.startsWith('/') ? BACKEND_URL + char.uploaded_photo_url : char.uploaded_photo_url}
                                      alt={`${char.name || 'Character'} illustration`}
                                      className="w-14 h-14 rounded-lg object-cover border-2 group-hover:opacity-80 transition-opacity"
                                      style={{ borderColor: '#9B59B6' }}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Eye className="w-5 h-5 text-white drop-shadow-lg" />
                                    </div>
                                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: '#9B59B6' }}>
                                      <Sparkles className="w-3 h-3 text-white" />
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-xs font-medium" style={{ color: 'var(--st-text-heading)' }}>Photo transformed!</p>
                                    <p className="text-xs" style={{ color: 'var(--st-text-muted)' }}>This character will appear in your story</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeCharacterPhoto(index)}
                                    className="text-xs px-2 py-1 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : char.pendingPreview ? (
                                // Show pending confirmation state
                                <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--st-bg-warning)', border: '1px solid #F59E0B40' }}>
                                  <img
                                    src={char.pendingPreview}
                                    alt="Preview"
                                    className="w-16 h-16 rounded-lg object-cover"
                                    style={{ border: '2px solid #F59E0B80' }}
                                  />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium" style={{ color: 'var(--st-text-heading)' }}>Ready to transform</p>
                                    <p className="text-xs" style={{ color: 'var(--st-text-muted)' }}>This photo will be converted to storybook style</p>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => cancelPendingPhoto(index)}
                                      className="px-3 py-1.5 text-xs rounded-lg transition-colors"
                                      style={{ background: 'var(--st-bg-neutral)', color: 'var(--st-text-neutral)' }}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => confirmPhotoUpload(index)}
                                      className="px-3 py-1.5 text-xs rounded-lg text-white flex items-center gap-1 transition-colors hover:opacity-90"
                                      style={{ background: '#9B59B6' }}
                                    >
                                      <Sparkles className="w-3 h-3" />
                                      Transform
                                    </button>
                                  </div>
                                </div>
                              ) : char.isUploading ? (
                                // Show uploading state
                                <div className="flex items-center gap-3 p-2 rounded-lg" style={{ background: 'var(--st-bg-processing)' }}>
                                  {char.photoPreview && (
                                    <img
                                      src={char.photoPreview}
                                      alt="Uploading"
                                      className="w-12 h-12 rounded-lg object-cover opacity-50"
                                    />
                                  )}
                                  <div className="flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--st-primary)' }} />
                                    <span className="text-xs" style={{ color: 'var(--st-text-muted)' }}>Transforming photo into storybook style...</span>
                                  </div>
                                </div>
                              ) : (
                                // Show upload button
                                <label className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors group" style={{ border: '1px dashed #D1D5DB' }}>
                                  <Camera className="w-4 h-4 group-hover:text-purple-500" style={{ color: '#9B59B6' }} />
                                  <span className="text-xs" style={{ color: 'var(--st-text-muted)' }}>
                                    Upload photo to transform into character
                                  </span>
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    className="hidden"
                                    disabled={loading}
                                    onChange={(e) => {
                                      if (e.target.files?.[0]) {
                                        handlePhotoSelect(index, e.target.files[0]);
                                        e.target.value = '';
                                      }
                                    }}
                                  />
                                  <span className="ml-auto text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-300">
                                    Premium
                                  </span>
                                </label>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="flex items-center gap-2 flex-wrap">
                        {customCharacters.length < 4 && (
                          <button
                            type="button"
                            onClick={addCharacter}
                            className="text-sm px-3 py-2 rounded-lg font-medium text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                          >
                            + Add Character
                          </button>
                        )}
                        {user?.is_premium && (
                          <button
                            type="button"
                            onClick={() => setShowSavedCharactersModal(true)}
                            className="text-sm px-3 py-2 rounded-lg font-medium flex items-center gap-1.5 transition-colors hover:bg-purple-50 dark:hover:bg-purple-900/20"
                            style={{ color: '#9B59B6' }}
                          >
                            <Library className="w-4 h-4" />
                            My Characters
                          </button>
                        )}
                      </div>
                    </div>
                  </CollapsibleSection>

                  {/* Story Details Section */}
                  <CollapsibleSection
                    title="Story Details"
                    icon={MessageSquare}
                    expanded={expandedSections.story}
                    onToggle={() => toggleSection('story')}
                  >
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-gray-500 mb-1.5 block">Setting</Label>
                          <Input
                            placeholder="e.g., Enchanted forest"
                            value={customSetting}
                            onChange={(e) => setCustomSetting(e.target.value)}
                            disabled={loading}
                            className="border rounded-lg h-10 text-sm"
                            style={{ borderColor: 'var(--st-border-medium)', background: 'var(--st-bg-muted)' }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500 mb-1.5 block">Theme</Label>
                          <Select value={customTheme} onValueChange={setCustomTheme} disabled={loading}>
                            <SelectTrigger className="border rounded-lg h-10 text-sm" style={{ borderColor: 'var(--st-border-medium)', background: 'var(--st-bg-muted)' }}>
                              <SelectValue placeholder="Select theme..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="friendship">Friendship</SelectItem>
                              <SelectItem value="courage">Courage</SelectItem>
                              <SelectItem value="kindness">Kindness</SelectItem>
                              <SelectItem value="perseverance">Perseverance</SelectItem>
                              <SelectItem value="honesty">Honesty</SelectItem>
                              <SelectItem value="creativity">Creativity</SelectItem>
                              <SelectItem value="family">Family</SelectItem>
                              <SelectItem value="faith">Faith</SelectItem>
                              <SelectItem value="love">Love</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500 mb-1.5 block">Moral / Lesson (optional)</Label>
                        <Input
                          placeholder="e.g., True friends accept you for who you are"
                          value={customMoral}
                          onChange={(e) => setCustomMoral(e.target.value)}
                          disabled={loading}
                          className="border rounded-lg h-10 text-sm"
                          style={{ borderColor: 'var(--st-border-medium)', background: 'var(--st-bg-muted)' }}
                        />
                      </div>
                    </div>
                  </CollapsibleSection>

                  {/* Style & Format Section */}
                  <CollapsibleSection
                    title="Style & Format"
                    icon={Palette}
                    expanded={expandedSections.style}
                    onToggle={() => toggleSection('style')}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500 mb-1.5 block">Narrative</Label>
                        <Select value={narrativeStyle} onValueChange={setNarrativeStyle} disabled={loading}>
                          <SelectTrigger className="border rounded-lg h-9 text-sm" style={{ borderColor: 'var(--st-border-medium)', background: 'var(--st-bg-muted)' }}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="prose">Prose</SelectItem>
                            <SelectItem value="rhyming">Rhyming</SelectItem>
                            <SelectItem value="poetic">Poetic</SelectItem>
                            <SelectItem value="simple">Simple</SelectItem>
                            <SelectItem value="dialogue-rich">Dialogue Rich</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500 mb-1.5 block">Tone</Label>
                        <Select value={narrativeTone} onValueChange={setNarrativeTone} disabled={loading}>
                          <SelectTrigger className="border rounded-lg h-9 text-sm" style={{ borderColor: 'var(--st-border-medium)', background: 'var(--st-bg-muted)' }}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="warm">Warm</SelectItem>
                            <SelectItem value="funny">Funny</SelectItem>
                            <SelectItem value="silly">Silly</SelectItem>
                            <SelectItem value="adventurous">Adventurous</SelectItem>
                            <SelectItem value="gentle">Gentle</SelectItem>
                            <SelectItem value="inspiring">Inspiring</SelectItem>
                            <SelectItem value="playful">Playful</SelectItem>
                            <SelectItem value="heartwarming">Heartwarming</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500 mb-1.5 block">Length</Label>
                        <Select value={storyLength} onValueChange={setStoryLength} disabled={loading || isFreeUser}>
                          <SelectTrigger className="border rounded-lg h-9 text-sm" style={{ borderColor: 'var(--st-border-medium)', background: 'var(--st-bg-muted)' }}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {currentLengthOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {isFreeUser && (
                          <p className="text-xs mt-1" style={{ color: 'var(--st-text-muted)' }}>
                            <Lock className="w-3 h-3 inline mr-1" />
                            Upgrade for more pages
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Chapter Book Time Warning */}
                    {ageRange === "8-12" && getPageCount() >= 30 && (
                      <div
                        className="flex items-start gap-2 p-3 rounded-lg mt-3 animate-in fade-in duration-200"
                        style={{ background: '#FEF3C7', border: '1px solid #FDE68A' }}
                      >
                        <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#D97706' }} />
                        <p className="text-xs" style={{ color: '#92400E' }}>
                          <span className="font-semibold">Longer creation time:</span> Chapter books with {getPageCount()} pages typically take 15-25 minutes to generate. Feel free to browse while we create your story!
                        </p>
                      </div>
                    )}
                  </CollapsibleSection>
                </div>
              )}

              {/* Version + Premium Options Row */}
              <div
                className="flex flex-wrap items-stretch rounded-xl overflow-hidden"
                style={{ border: '1px solid var(--st-border-light)' }}
              >
                {/* Version Badge */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 px-3 py-2.5 cursor-help ${
                        (selectedVersion || getDefaultVersion()) === 3
                          ? 'bg-purple-100 dark:bg-purple-900/40'
                          : (selectedVersion || getDefaultVersion()) === 2
                          ? 'bg-blue-100 dark:bg-blue-900/40'
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    >
                      {(selectedVersion || getDefaultVersion()) === 3 ? (
                        <Star className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      ) : (
                        <Sparkles className="w-4 h-4" style={{
                          color: (selectedVersion || getDefaultVersion()) === 2 ? '#3B82F6' : '#6B7280'
                        }} />
                      )}
                      <span className={`text-sm font-semibold ${
                        (selectedVersion || getDefaultVersion()) === 3
                          ? 'text-purple-700 dark:text-purple-300'
                          : (selectedVersion || getDefaultVersion()) === 2
                          ? 'text-blue-700 dark:text-blue-300'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {(selectedVersion || getDefaultVersion()) === 3 ? 'v3 Pro'
                          : (selectedVersion || getDefaultVersion()) === 2 ? 'v2 Premium'
                          : 'v1 Standard'}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      {(selectedVersion || getDefaultVersion()) === 3
                        ? 'Best quality with superior character consistency across all pages'
                        : (selectedVersion || getDefaultVersion()) === 2
                        ? 'High-quality illustrations with good character matching'
                        : 'Standard illustrations — characters may vary between pages'}
                    </p>
                  </TooltipContent>
                </Tooltip>

                {/* Animations Toggle (Premium only, v2+, not chapter books) */}
                {user?.is_premium && (selectedVersion || getDefaultVersion()) >= 2 && ageRange !== "8-12" && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className={`flex-1 min-w-[160px] flex items-center justify-center gap-2 px-3 py-2.5 border-l border-t sm:border-t-0 transition-all cursor-help ${
                        enableAnimations ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-gray-50 dark:bg-gray-800/30'
                      }`} style={{ borderColor: 'var(--st-border-light)' }}>
                        <Film className="w-4 h-4" style={{ color: enableAnimations ? '#D97706' : 'var(--st-text-muted)' }} />
                        <span className={`text-sm font-medium ${enableAnimations ? 'text-amber-700 dark:text-amber-300' : 'text-gray-600 dark:text-gray-400'}`}>
                          Animations
                        </span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                          enableAnimations ? 'bg-amber-200 text-amber-700' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                        }`}>
                          +1
                        </span>
                        <Switch
                          checked={enableAnimations}
                          onCheckedChange={setEnableAnimations}
                          className="data-[state=checked]:bg-amber-500 scale-90"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">Key scenes gently animate — perfect for bedtime reading</p>
                    </TooltipContent>
                  </Tooltip>
                )}

                {/* Voice Selector (Premium only) */}
                {user?.is_premium && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-3 py-2.5 border-l border-t sm:border-t-0 bg-gray-50 dark:bg-gray-800/30 cursor-help" style={{ borderColor: 'var(--st-border-light)' }}>
                        <Mic className="w-4 h-4 flex-shrink-0" style={{ color: '#8B5CF6' }} />
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 flex-shrink-0">Voice</span>
                        <Select value={narratorVoice} onValueChange={setNarratorVoice}>
                          <SelectTrigger className="w-[70px] h-7 text-sm border-0 bg-transparent p-0 pl-1 focus:ring-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nova">Sarah</SelectItem>
                            <SelectItem value="shimmer">Grace</SelectItem>
                            <SelectItem value="onyx">Oliver</SelectItem>
                            <SelectItem value="echo">Max</SelectItem>
                            <SelectItem value="alloy">Riley</SelectItem>
                            <SelectItem value="fable">Pip</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">Choose the narrator voice for "Read to Me" audio</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              {/* Generate Button */}
              {!loading ? (
                <button
                  data-testid="generate-story-btn"
                  onClick={handleGenerate}
                  disabled={!prompt.trim()}
                  className="w-full flex items-center justify-center gap-2 group relative overflow-hidden"
                  style={{
                    background: prompt.trim()
                      ? 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 50%, var(--st-primary) 100%)'
                      : 'var(--st-bg-muted)',
                    color: prompt.trim() ? 'white' : 'var(--st-text-muted)',
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    border: 'none',
                    borderRadius: '12px',
                    fontFamily: 'Chewy, cursive',
                    cursor: prompt.trim() ? 'pointer' : 'not-allowed',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Wand2 className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
                  <span>Create Story</span>
                  <span
                    className="ml-1 px-2 py-0.5 rounded-full text-sm font-medium"
                    style={{
                      background: 'rgba(255, 255, 255, 0.25)',
                      backdropFilter: 'blur(4px)'
                    }}
                  >
                    {getStoryCost()} {getStoryCost() === 1 ? 'credit' : 'credits'}
                  </span>
                </button>
              ) : (
                <div className="rounded-2xl p-5 shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--st-primary)' }} />
                    <span className="font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                      {progress}
                    </span>
                  </div>
                  <div className="relative w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--st-border-light)' }}>
                    <div
                      className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000"
                      style={{
                        width: `${progressPercent}%`,
                        background: 'linear-gradient(90deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                      }}
                    />
                  </div>
                  <p className="text-center text-xs text-gray-500 mt-3" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                    Usually takes 2-3 minutes
                  </p>
                </div>
              )}

              {/* AI Disclaimer */}
              <div className="mt-4 px-4 py-3 rounded-xl" style={{ background: 'var(--st-bg-subtle)', border: '1px solid var(--st-border-light)' }}>
                <p className="text-xs text-center leading-relaxed" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  <span className="font-semibold">AI-Generated Content:</span> Stories are created using artificial intelligence and may contain errors, inconsistencies, or unexpected content. Please review stories before sharing with children. Parental guidance is recommended.
                </p>
              </div>

            </div>

            {/* Right Column - Preview Panel or Workspace */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-6">
                {showPreviewPanel && activeJob ? (
                  <div className="rounded-2xl overflow-hidden shadow-lg animate-in slide-in-from-right-4 duration-300" style={{ background: 'var(--st-bg-surface)', border: '2px solid var(--st-border-light)' }}>
                    <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--st-border-light)', background: 'linear-gradient(135deg, var(--st-bg-subtle) 0%, var(--st-border-light) 100%)' }}>
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                        <span className="font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                          Creating Your Story
                        </span>
                      </div>
                      <button
                        onClick={handleClosePreview}
                        className="p-1.5 rounded-lg hover:bg-orange-100 dark:hover:bg-white/10 transition-colors"
                        title={activeJob.status === 'queued' ? 'Cancel and refund' : 'Close'}
                      >
                        <X className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                      </button>
                    </div>

                    <div className="relative aspect-[3/4] bg-gradient-to-br from-orange-100 to-amber-100">
                      {activeJob.thumbnail_url ? (
                        <img src={activeJob.thumbnail_url} alt="Story preview" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-center">
                            <Sparkles className="w-12 h-12 mx-auto mb-2" style={{ color: 'var(--st-primary-hover)' }} />
                            <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                              Creating preview...
                            </p>
                          </div>
                        </div>
                      )}

                      {activeJob.status !== 'failed' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <div className="bg-white/90 dark:bg-black/70 rounded-full p-3 shadow-lg">
                            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--st-primary)' }} />
                          </div>
                        </div>
                      )}

                      {activeJob.status === 'failed' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <div className="rounded-xl p-4 mx-4 text-center" style={{ background: 'var(--st-bg-surface)' }}>
                            <XCircle className="w-10 h-10 mx-auto mb-2 text-red-400" />
                            <p className="text-sm font-medium text-red-600">{activeJob.error || 'Generation failed'}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-4 space-y-3">
                      {activeJob.status === 'queued' && activeJob.queue_position && (
                        <div className="text-center py-2 px-4 rounded-xl" style={{ background: 'var(--st-bg-subtle)' }}>
                          <span className="text-2xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-primary)' }}>
                            #{activeJob.queue_position}
                          </span>
                          <span className="text-sm ml-2" style={{ color: 'var(--st-text-muted)' }}>in queue</span>
                        </div>
                      )}

                      {activeJob.status === 'processing' && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span style={{ color: 'var(--st-text-muted)' }}>{activeJob.message || 'Processing...'}</span>
                            <span className="font-bold" style={{ color: 'var(--st-primary)' }}>{activeJob.progress || 0}%</span>
                          </div>
                          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--st-border-light)' }}>
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${activeJob.progress || 0}%`, background: 'linear-gradient(90deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)' }}
                            />
                          </div>
                        </>
                      )}

                      <div className="pt-2 border-t" style={{ borderColor: 'var(--st-border-light)' }}>
                        <p className="text-xs text-gray-500 line-clamp-2">{activeJob.prompt}</p>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => navigate(`/generating/${activeJob.id}`)}
                          className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-xl text-sm font-medium transition-colors"
                          style={{ background: 'var(--st-border-light)', color: 'var(--st-text-heading)' }}
                        >
                          <Eye className="w-4 h-4" />
                          View Details
                        </button>
                        {activeJob.status === 'queued' && (
                          <button
                            onClick={handleClosePreview}
                            className="py-2 px-4 rounded-xl text-sm font-medium text-red-500 border-2 border-red-200 hover:bg-red-50 transition-colors"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <MyStoriesWorkspace
                    key={workspaceRefreshKey}
                    onStoryClick={(storyId) => navigate(`/story/${storyId}`)}
                    onJobClick={(jobId) => navigate(`/generating/${jobId}`)}
                    onRecreate={handleRecreate}
                    onReimagine={handleReimagine}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enlarged Photo Modal */}
      {enlargedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setEnlargedPhoto(null)}
        >
          <div className="relative max-w-2xl max-h-[80vh]">
            <button
              onClick={() => setEnlargedPhoto(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={enlargedPhoto}
              alt="Enlarged character"
              className="max-w-full max-h-[80vh] rounded-lg object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />

      {/* Saved Characters Library Modal */}
      <SavedCharactersLibrary
        isOpen={showSavedCharactersModal}
        onClose={() => setShowSavedCharactersModal(false)}
        onSelectCharacter={handleSelectSavedCharacter}
      />

      {/* Save Character Modal */}
      {pendingSaveCharacter && (
        <SaveCharacterModal
          isOpen={showSaveCharacterModal}
          characterName={pendingSaveCharacter.name}
          portraitUrl={pendingSaveCharacter.portraitUrl}
          artStyle={pendingSaveCharacter.artStyle}
          colorPalette={pendingSaveCharacter.colorPalette}
          onSave={handleSaveCharacterClose}
          onSkip={handleSaveCharacterClose}
        />
      )}
    </PageLayout>
  );
};

export default CreatePage;
